import { DomainError } from '../errors/domain-error';
import { assertMarketingTimezone } from './marketing-edition-schedule';

export const MarketingSequenceStatus = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const;

export type MarketingSequenceStatusValue =
  (typeof MarketingSequenceStatus)[keyof typeof MarketingSequenceStatus];

export const MARKETING_SEQUENCE_STATUS_VALUES = Object.values(MarketingSequenceStatus);

export const MarketingSequenceTrigger = {
  MANUAL: 'manual',
  WELCOME_CONFIRM: 'welcome_confirm',
} as const;

export type MarketingSequenceTriggerValue =
  (typeof MarketingSequenceTrigger)[keyof typeof MarketingSequenceTrigger];

export const MARKETING_SEQUENCE_TRIGGER_VALUES = Object.values(MarketingSequenceTrigger);

export const MARKETING_SEQUENCE_MAX_STEPS = 20;
export const MARKETING_SEQUENCE_MAX_DELAY_MINUTES = 60 * 24 * 90; // 90 days

/** Scale delays for local/staging accelerated clocks (`1` = real time). */
export function marketingSequenceDelayScale(): number {
  const raw = Number(process.env.MARKETING_SEQUENCE_DELAY_SCALE ?? '1');
  if (!Number.isFinite(raw) || raw < 0) return 1;
  return raw;
}

export function assertMarketingSequenceStatus(value: string): MarketingSequenceStatusValue {
  if (!(MARKETING_SEQUENCE_STATUS_VALUES as string[]).includes(value)) {
    throw new DomainError( 'Invalid sequence status');
  }
  return value as MarketingSequenceStatusValue;
}

export function assertMarketingSequenceTrigger(value: string): MarketingSequenceTriggerValue {
  if (!(MARKETING_SEQUENCE_TRIGGER_VALUES as string[]).includes(value)) {
    throw new DomainError( 'Invalid sequence trigger');
  }
  return value as MarketingSequenceTriggerValue;
}

export function assertMarketingSequenceDelayMinutes(value: number): number {
  if (!Number.isInteger(value) || value < 0 || value > MARKETING_SEQUENCE_MAX_DELAY_MINUTES) {
    throw new DomainError(
      `Delay must be 0–${MARKETING_SEQUENCE_MAX_DELAY_MINUTES} minutes`,
    );
  }
  return value;
}

export function assertMarketingSendWindowHours(
  startHour: number | null | undefined,
  endHour: number | null | undefined,
): { startHour: number | null; endHour: number | null } {
  const start = startHour == null ? null : startHour;
  const end = endHour == null ? null : endHour;
  if (start == null && end == null) return { startHour: null, endHour: null };
  if (start == null || end == null) {
    throw new DomainError( 'Send window requires both start and end hour');
  }
  if (!Number.isInteger(start) || start < 0 || start > 23) {
    throw new DomainError( 'Send window start hour must be 0–23');
  }
  if (!Number.isInteger(end) || end < 0 || end > 23) {
    throw new DomainError( 'Send window end hour must be 0–23');
  }
  if (start === end) {
    throw new DomainError( 'Send window start and end must differ');
  }
  return { startHour: start, endHour: end };
}

function hourInTimezone(at: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(at);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
  return Number.isFinite(hour) ? hour : 0;
}

function isWithinSendWindow(at: Date, timezone: string, startHour: number, endHour: number) {
  const hour = hourInTimezone(at, timezone);
  if (startHour < endHour) {
    return hour >= startHour && hour < endHour;
  }
  // Overnight window (e.g. 22 → 6)
  return hour >= startHour || hour < endHour;
}

/**
 * Compute next runnable instant after `from`, applying delay scale and optional send window.
 */
export function computeMarketingSequenceNextRunAt(input: {
  from: Date;
  delayMinutes: number;
  timezone?: string | null;
  sendWindowStartHour?: number | null;
  sendWindowEndHour?: number | null;
  delayScale?: number;
}): Date {
  const delay = assertMarketingSequenceDelayMinutes(input.delayMinutes);
  const scale = input.delayScale ?? marketingSequenceDelayScale();
  const scaledMs = Math.round(delay * 60_000 * scale);
  let candidate = new Date(input.from.getTime() + scaledMs);

  const window = assertMarketingSendWindowHours(
    input.sendWindowStartHour,
    input.sendWindowEndHour,
  );
  if (window.startHour == null || window.endHour == null) {
    return candidate;
  }

  const timezone = assertMarketingTimezone(input.timezone?.trim() || 'UTC');
  // Advance in 15-minute steps until inside window (max 48h guard).
  for (let i = 0; i < 48 * 4; i += 1) {
    if (isWithinSendWindow(candidate, timezone, window.startHour, window.endHour)) {
      return candidate;
    }
    candidate = new Date(candidate.getTime() + 15 * 60_000);
  }
  return candidate;
}
